import { Meeting_Step_Type_Enum } from '@gql'

// Organizational models offered during onboarding. The chosen value is stored
// on org.onboardingOrgType for stats only. Each model defines its base
// governance roles (created once for the org) and its meeting templates.
//
// Nothing is scheduled: only meeting templates are created, and people plan
// their own meetings (decided with Yohan, 21/09/2026). `HolacracyV4` was
// dropped from the choices; existing orgs may still carry it as a stat value.
export enum OrgType {
  Empty = 'Empty',
  Classic = 'Classic',
  UdN = 'UdN',
  Sociocracy = 'Sociocracy',
  HolacracyV5 = 'HolacracyV5',
}

// i18n key suffix for base role names (literal union so the composed
// `Onboarding.roles.*` key type-checks without a cast).
type RoleNameKey =
  | 'manager'
  | 'representative'
  | 'circleLead'
  | 'circleRep'
  | 'leader'
  | 'delegate'
  | 'secretary'
  | 'facilitator'

// i18n key suffix for meeting template titles.
type MeetingTitleKey =
  | 'teamWeekly'
  | 'retrospective'
  | 'tactical'
  | 'governance'
  | 'operational'
  | 'policy'

export interface SeedBaseRole {
  nameKey: RoleNameKey
  colorHue: number
  parentLink?: boolean
}

export interface SeedMeetingTemplate {
  titleKey: MeetingTitleKey
  steps: Meeting_Step_Type_Enum[]
}

export interface OrgTypePreset {
  labelKey: `OrgTypeSelect.${OrgType}`
  helpKey: `OrgTypeSelect.${OrgType}_desc`
  baseRoles: SeedBaseRole[]
  meetingTemplates: SeedMeetingTemplate[]
}

const Step = Meeting_Step_Type_Enum

// Operational (weekly) and governance (monthly) steps of circle-based models
const operationalSteps = [
  Step.Tour,
  Step.Checklist,
  Step.Indicators,
  Step.Tasks,
  Step.Threads,
]
const governanceSteps = [Step.Tour, Step.ThreadsGovernance]

// Circle roles shared by every circle-based model
const circleLead: SeedBaseRole = {
  nameKey: 'circleLead',
  colorHue: 0,
  parentLink: true,
}
const circleRep: SeedBaseRole = {
  nameKey: 'circleRep',
  colorHue: 18,
  parentLink: true,
}
const secretary: SeedBaseRole = { nameKey: 'secretary', colorHue: 283 }
const facilitator: SeedBaseRole = { nameKey: 'facilitator', colorHue: 111 }

export const orgTypePresets: Record<OrgType, OrgTypePreset> = {
  // Only the root circle, with its creator in it
  [OrgType.Empty]: {
    labelKey: 'OrgTypeSelect.Empty',
    helpKey: 'OrgTypeSelect.Empty_desc',
    baseRoles: [],
    meetingTemplates: [],
  },

  [OrgType.Classic]: {
    labelKey: 'OrgTypeSelect.Classic',
    helpKey: 'OrgTypeSelect.Classic_desc',
    baseRoles: [
      { nameKey: 'manager', colorHue: 0, parentLink: true },
      { nameKey: 'representative', colorHue: 18, parentLink: true },
    ],
    meetingTemplates: [
      { titleKey: 'teamWeekly', steps: [Step.Tour, Step.Tasks, Step.Threads] },
      { titleKey: 'retrospective', steps: [Step.Tour, Step.Threads] },
    ],
  },

  // The vocabulary used across the UdN orgs (read from its data, 21/09/2026)
  [OrgType.UdN]: {
    labelKey: 'OrgTypeSelect.UdN',
    helpKey: 'OrgTypeSelect.UdN_desc',
    baseRoles: [circleLead, circleRep, secretary, facilitator],
    meetingTemplates: [
      { titleKey: 'operational', steps: operationalSteps },
      { titleKey: 'governance', steps: governanceSteps },
    ],
  },

  [OrgType.Sociocracy]: {
    labelKey: 'OrgTypeSelect.Sociocracy',
    helpKey: 'OrgTypeSelect.Sociocracy_desc',
    baseRoles: [
      { nameKey: 'leader', colorHue: 0, parentLink: true },
      { nameKey: 'delegate', colorHue: 18, parentLink: true },
      secretary,
      facilitator,
    ],
    meetingTemplates: [
      { titleKey: 'operational', steps: operationalSteps },
      { titleKey: 'policy', steps: governanceSteps },
    ],
  },

  [OrgType.HolacracyV5]: {
    labelKey: 'OrgTypeSelect.HolacracyV5',
    helpKey: 'OrgTypeSelect.HolacracyV5_desc',
    baseRoles: [circleLead, circleRep, secretary, facilitator],
    meetingTemplates: [
      { titleKey: 'tactical', steps: operationalSteps },
      { titleKey: 'governance', steps: governanceSteps },
    ],
  },
}

// The vocabularies offered under the "Shared governance" card; the first one
// is preselected.
export const sharedGovernanceVocabularies = [
  OrgType.UdN,
  OrgType.Sociocracy,
  OrgType.HolacracyV5,
] as const

// The leader role of a model (the role the person responsible for a circle
// occupies): the first parent-linking base role. Empty has none and never
// reaches the roles step; fall back to Classic's so the label still reads.
export function getRepresentativeRole(orgType: OrgType): SeedBaseRole {
  const roles = orgTypePresets[orgType].baseRoles
  return (
    roles.find((r) => r.parentLink) ??
    roles[0] ??
    orgTypePresets[OrgType.Classic].baseRoles[0]
  )
}
